/**
 * Module 'machine-load' (ADR 0063 step 4, visibility): what is using each machine, always on.
 *
 *   agent -> hub  { ext: 'machine-load', type: 'report', data: MachineLoadReport }
 *   GET /api/state  state.ext['machine-load']: MachineLoadState
 *   SSE 'ext'       { ext: 'machine-load', type: 'state', data: MachineLoadState } after each report
 *
 * Two facts per machine: the work budget (the leases and waiters `orgus work` keeps as files in
 * the lease folder, ADR 0063 decision 3) and a CPU breakdown that charges every busy process to
 * its work lease, else its session, else its editor, else the project its command line names,
 * else a named kind of background work. Shares are of the whole machine (0..100).
 */
import type { Id, Ts } from '../index.ts';

export const MACHINE_LOAD_EXT = 'machine-load';
export const MACHINE_LOAD_FRAME = 'report';

/** Who a share of the CPU is charged to. */
export type LoadKind =
  | 'work'
  | 'session'
  | 'project'
  | 'editor'
  | 'antivirus'
  | 'indexer'
  | 'browser'
  | 'system'
  | 'orgus'
  | 'other'
  | 'short-lived';

/** Kinds no session or lease started: the machine's own background. */
export const BACKGROUND_LOAD_KINDS: readonly LoadKind[] = ['editor', 'antivirus', 'indexer', 'browser', 'system', 'orgus', 'other'];

export const LOAD_KIND_LABEL: Record<LoadKind, string> = {
  work: 'Heavy work',
  session: 'Session',
  project: 'Project',
  editor: 'Editors',
  antivirus: 'Antivirus',
  indexer: 'Search indexer',
  browser: 'Browsers',
  system: 'Windows and services',
  orgus: 'Orgus',
  other: 'Other apps',
  'short-lived': 'Short-lived processes',
};

/** One group of processes and its share of the machine. */
export interface LoadGroup {
  kind: LoadKind;
  /** What it is: the lease's command ('pnpm test:affected'), the project folder, 'Defender'. */
  label: string;
  /** Share of the whole machine over the window, 0..100. */
  pct: number;
  processes: number;
  /** The tools inside it, busiest first, at most 3 ('vitest', 'tsc'). */
  tools?: string[];
  leaseId?: string;
  sessionId?: Id;
  projectId?: Id;
}

/** A lease of CPU slots held by a heavy command (`orgus work run`, `orgus land`). */
export interface WorkLeaseView {
  id: string;
  pid: number;
  slots: number;
  what: string;
  cwd: string;
  since: Ts;
  /** The live session the command runs under, when it still does. */
  sessionId?: Id;
  /** The project whose checkout `cwd` is in. */
  projectId?: Id;
}

/** A heavy command waiting for slots. */
export interface WorkWaiterView {
  id: string;
  pid: number;
  want: number;
  min: number;
  what: string;
  cwd: string;
  since: Ts;
  sessionId?: Id;
  projectId?: Id;
}

/** The machine's work budget as the lease folder says now. */
export interface WorkBudgetView {
  /** Slots heavy work may use: the cores minus a reserve (ORGUS_WORK_SLOTS_TOTAL overrides). */
  budget: number;
  used: number;
  /** Oldest first. */
  leases: WorkLeaseView[];
  /** In queue order. */
  waiting: WorkWaiterView[];
}

export interface MachineLoadTotals {
  work: number;
  sessions: number;
  projects: number;
  /** Every background kind together (BACKGROUND_LOAD_KINDS). */
  background: number;
  antivirus: number;
  shortLived: number;
}

export interface MachineLoadReport {
  at: Ts;
  /** Over how long the shares were measured; 0 before the first full window. */
  windowMs: number;
  /**
   * When the breakdown was taken, on the agent's clock: a report sent again only because the
   * leases changed carries the same value. 0 before the first breakdown.
   */
  sampleAt: Ts;
  /** Machine busy fraction over the window, 0..1. */
  load: number;
  cpus: number;
  /** Busiest first, at most MACHINE_LOAD_MAX_GROUPS. */
  groups: LoadGroup[];
  totals: MachineLoadTotals;
  /** null when the lease folder could not be read. */
  work: WorkBudgetView | null;
}

/** The hub's view of one machine: the latest report and whether antivirus has stayed high. */
export interface MachineLoadEntry extends MachineLoadReport {
  machineId: Id;
  /** Antivirus has used at least MACHINE_LOAD_AV_HIGH_PCT of the machine for MACHINE_LOAD_AV_SUSTAIN_MS. */
  antivirusHigh?: { since: Ts; avgPct: number };
}

export interface MachineLoadState {
  machines: MachineLoadEntry[];
}

export const MACHINE_LOAD_MAX_GROUPS = 10;
/** How often the agent sends a breakdown: every 30 s, every 15 s while the machine is busy. */
export const MACHINE_LOAD_REPORT_MS = 30_000;
export const MACHINE_LOAD_BUSY_REPORT_MS = 15_000;
export const MACHINE_LOAD_BUSY = 0.8;
/** A report older than this is not shown as live. */
export const MACHINE_LOAD_TTL_MS = 2 * 60_000;
export const MACHINE_LOAD_AV_HIGH_PCT = 10;
export const MACHINE_LOAD_AV_SUSTAIN_MS = 5 * 60_000;

/** Slots heavy work may use on a machine with `cpus` cores (the same rule as `orgus work`). */
export const workBudgetFor = (cpus: number): number => Math.max(2, cpus - Math.max(2, Math.ceil(cpus / 4)));

/** The top `n` drivers of a machine's load, for a tooltip or one line: 'pnpm test 34%, Antivirus 12%'. */
export function topDrivers(r: Pick<MachineLoadReport, 'groups'>, n = 3): LoadGroup[] {
  return r.groups.filter((g) => g.pct >= 1).slice(0, n);
}
